// Food Program data layer.
//
// The meal-participation record is canonical for food-program operations.
// Attendance is used only to offer the teacher a checked-in roster; it is
// never silently rewritten here. The live RPCs enforce the organization,
// role, classroom, lock, and exception rules in Postgres.

import { supabase, isSupabaseConfigured } from "./supabase";
import {
  DEMO_CHILDREN,
  DEMO_CLASSROOMS,
  DEMO_TEACHER_CLASSROOM_IDS,
} from "./dailyReportsDemo";
import { todayStr } from "./entryTypes";

export const FOOD_MEAL_TYPES = Object.freeze([
  { key: "breakfast", label: "Breakfast" },
  { key: "am_snack", label: "AM snack" },
  { key: "lunch", label: "Lunch" },
  { key: "pm_snack", label: "PM snack" },
  { key: "dinner", label: "Dinner" },
  { key: "late_snack", label: "Late snack" },
]);

const DEMO_ORGANIZATION_ID = "00000000-0000-0000-0000-000000000001";
const DEMO_STORE_KEY = "daycare.demo.food-program.v1";

export function foodMealLabel(value) {
  return FOOD_MEAL_TYPES.find((meal) => meal.key === value)?.label || value || "Meal";
}

function ensureLive() {
  if (!isSupabaseConfigured) {
    throw new Error("This action requires a live HoneyAcorn environment.");
  }
}

function unwrap({ data, error }) {
  if (error) throw error;
  return data;
}

function cleanText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function localDateTime(date, hour, minute = 0) {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day, hour, minute, 0, 0).toISOString();
}

function demoDefaultState() {
  return {
    programs: [
      {
        id: "demo-food-program",
        organization_id: DEMO_ORGANIZATION_ID,
        name: "Center food program",
        configuration: {},
        is_active: true,
        created_at: new Date().toISOString(),
      },
    ],
    categories: [
      {
        id: "demo-food-category-standard",
        organization_id: DEMO_ORGANIZATION_ID,
        program_id: "demo-food-program",
        code: "standard",
        label: "Center-defined category",
        is_active: true,
        sort_order: 100,
      },
    ],
    templates: [
      {
        id: "demo-food-template-breakfast",
        organization_id: DEMO_ORGANIZATION_ID,
        program_id: "demo-food-program",
        meal_type: "breakfast",
        name: "Morning table",
        description: "Center-planned breakfast",
        is_active: true,
        items: [
          { id: "demo-food-item-breakfast-1", component_label: "Main", item_label: "Oatmeal", sort_order: 10 },
          { id: "demo-food-item-breakfast-2", component_label: "Fruit", item_label: "Seasonal fruit", sort_order: 20 },
        ],
      },
      {
        id: "demo-food-template-lunch",
        organization_id: DEMO_ORGANIZATION_ID,
        program_id: "demo-food-program",
        meal_type: "lunch",
        name: "Garden lunch",
        description: "Center-planned lunch",
        is_active: true,
        items: [
          { id: "demo-food-item-lunch-1", component_label: "Main", item_label: "Pasta primavera", sort_order: 10 },
          { id: "demo-food-item-lunch-2", component_label: "Vegetable", item_label: "Peas", sort_order: 20 },
          { id: "demo-food-item-lunch-3", component_label: "Fruit", item_label: "Strawberries", sort_order: 30 },
        ],
      },
      {
        id: "demo-food-template-pm-snack",
        organization_id: DEMO_ORGANIZATION_ID,
        program_id: "demo-food-program",
        meal_type: "pm_snack",
        name: "Afternoon snack",
        description: "Center-planned snack",
        is_active: true,
        items: [
          { id: "demo-food-item-snack-1", component_label: "Snack", item_label: "Yogurt and crackers", sort_order: 10 },
        ],
      },
    ],
    eligibilities: [],
    accommodations: [
      {
        id: "demo-food-accommodation-noa",
        organization_id: DEMO_ORGANIZATION_ID,
        child_id: "00000000-0000-0000-0000-000000000301",
        label: "Peanut-free meal",
        teacher_instruction: "Use the labeled peanut-free serving and avoid shared utensils.",
        starts_on: "2020-01-01",
        ends_on: null,
        is_active: true,
      },
    ],
    services: [],
    participations: [],
  };
}

function readDemoState() {
  if (typeof window === "undefined" || !window.localStorage) return demoDefaultState();
  try {
    const stored = window.localStorage.getItem(DEMO_STORE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // Fall through to a clean demo state when storage is unavailable/corrupt.
  }
  const seeded = demoDefaultState();
  writeDemoState(seeded);
  return seeded;
}

function writeDemoState(state) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(DEMO_STORE_KEY, JSON.stringify(state));
  } catch {
    // The demo stays usable for the current interaction even without storage.
  }
}

function demoId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function demoActiveProgram(state) {
  return state.programs.find((program) => program.is_active) || null;
}

function demoMealServiceSnapshot(state, serviceId) {
  const service = state.services.find((row) => row.id === serviceId);
  if (!service) throw new Error("Meal service not found.");
  const template = state.templates.find((row) => row.id === service.menu_template_id) || null;
  const children = DEMO_CHILDREN.filter((child) => child.classroom_id === service.classroom_id);
  const participations = state.participations.filter((row) => row.meal_service_id === service.id);
  const roster = children.map((child) => {
    const participation = participations.find((row) => row.child_id === child.id) || null;
    const attendanceEligible = Boolean(child.id !== "00000000-0000-0000-0000-000000000304");
    return {
      child_id: child.id,
      first_name: child.first_name,
      last_name: child.last_name,
      allergies: child.allergies || null,
      attendance_record_id: attendanceEligible ? `demo-attendance-${service.service_date}-${child.id}` : null,
      attendance_status: attendanceEligible ? "present" : "absent",
      check_in_at: attendanceEligible ? localDateTime(service.service_date, 8, 30) : null,
      check_out_at: null,
      attendance_eligible: attendanceEligible,
      participation_id: participation?.id || null,
      participation_status: participation?.participation_status || null,
      has_attendance_exception: Boolean(participation?.attendance_exception_note),
      accommodations: state.accommodations
        .filter(
          (row) =>
            row.child_id === child.id &&
            row.is_active &&
            row.starts_on <= service.service_date &&
            (!row.ends_on || row.ends_on >= service.service_date),
        )
        .map(({ id, label, teacher_instruction }) => ({ id, label, teacher_instruction })),
    };
  });
  const counted = participations.filter((row) => ["served", "substituted"].includes(row.participation_status));
  return {
    service: {
      ...service,
      menu: template
        ? {
            id: template.id,
            name: template.name,
            description: template.description,
            items: template.items || [],
          }
        : null,
    },
    summary: {
      served_count: participations.filter((row) => row.participation_status === "served").length,
      substituted_count: participations.filter((row) => row.participation_status === "substituted").length,
      not_served_count: participations.filter((row) => row.participation_status === "not_served").length,
      counted_total: counted.length,
      attendance_exception_count: participations.filter((row) => row.attendance_exception_note).length,
    },
    roster,
  };
}

function demoTeacherDaySnapshot(date) {
  const state = readDemoState();
  const program = demoActiveProgram(state);
  return {
    service_date: date,
    program: program ? { id: program.id, name: program.name } : null,
    classrooms: DEMO_CLASSROOMS.filter((row) => DEMO_TEACHER_CLASSROOM_IDS.includes(row.id)),
    templates: program
      ? state.templates
          .filter((row) => row.program_id === program.id && row.is_active)
          .map(({ id, name, meal_type, description }) => ({ id, name, meal_type, description }))
      : [],
    services: program
      ? state.services
          .filter((row) => row.program_id === program.id && row.service_date === date && DEMO_TEACHER_CLASSROOM_IDS.includes(row.classroom_id))
          .map((row) => ({
            id: row.id,
            classroom_id: row.classroom_id,
            service_date: row.service_date,
            meal_type: row.meal_type,
            status: row.status,
            menu_template_id: row.menu_template_id,
            menu_name: state.templates.find((template) => template.id === row.menu_template_id)?.name || null,
            service_started_at: row.service_started_at,
            service_completed_at: row.service_completed_at,
            counted_participations: state.participations.filter(
              (participation) => participation.meal_service_id === row.id && ["served", "substituted"].includes(participation.participation_status),
            ).length,
          }))
      : [],
  };
}

function normalizeSetup(state) {
  return {
    programs: state.programs.slice().sort((a, b) => Number(b.is_active) - Number(a.is_active)),
    categories: state.categories.slice().sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label)),
    templates: state.templates.slice().sort((a, b) => a.meal_type.localeCompare(b.meal_type) || a.name.localeCompare(b.name)),
    eligibilities: state.eligibilities.slice(),
    accommodations: state.accommodations.slice(),
    classrooms: DEMO_CLASSROOMS.slice(),
    children: DEMO_CHILDREN.slice(),
  };
}

export async function getTeacherFoodDay(date = todayStr()) {
  if (!isSupabaseConfigured) return demoTeacherDaySnapshot(date);
  return unwrap(
    await supabase.rpc("rpc_food_teacher_day_snapshot", {
      p_service_date: date,
    }),
  );
}

export async function openMealService({ classroomId, serviceDate, mealType, menuTemplateId = null }) {
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const program = demoActiveProgram(state);
    if (!program) throw new Error("Food Program is not configured for this center.");
    let service = state.services.find(
      (row) => row.classroom_id === classroomId && row.service_date === serviceDate && row.meal_type === mealType,
    );
    if (!service) {
      service = {
        id: demoId("demo-food-service"),
        organization_id: DEMO_ORGANIZATION_ID,
        program_id: program.id,
        classroom_id: classroomId,
        service_date: serviceDate,
        meal_type: mealType,
        menu_template_id: menuTemplateId || null,
        status: "in_service",
        service_started_at: new Date().toISOString(),
        service_completed_at: null,
        locked_at: null,
        locked_by: null,
        lock_reason: null,
      };
      state.services.push(service);
    } else if (service.status === "locked") {
      throw new Error("This meal service has been locked after review.");
    } else {
      service.status = service.status === "planned" ? "in_service" : service.status;
      service.service_started_at = service.service_started_at || new Date().toISOString();
      if (menuTemplateId) service.menu_template_id = menuTemplateId;
    }
    writeDemoState(state);
    return demoMealServiceSnapshot(state, service.id);
  }
  return unwrap(
    await supabase.rpc("rpc_food_open_meal_service", {
      p_classroom_id: classroomId,
      p_service_date: serviceDate,
      p_meal_type: mealType,
      p_menu_template_id: menuTemplateId || null,
    }),
  );
}

export async function getMealServiceSnapshot(serviceId) {
  if (!isSupabaseConfigured) return demoMealServiceSnapshot(readDemoState(), serviceId);
  return unwrap(
    await supabase.rpc("rpc_food_meal_service_snapshot", {
      p_meal_service_id: serviceId,
    }),
  );
}

export async function recordCheckedInRoster(serviceId) {
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const snapshot = demoMealServiceSnapshot(state, serviceId);
    if (snapshot.service.status === "locked") throw new Error("This meal service has been locked after review.");
    snapshot.roster
      .filter((child) => child.attendance_eligible && !child.participation_id)
      .forEach((child) => {
        state.participations.push({
          id: demoId("demo-food-participation"),
          meal_service_id: serviceId,
          child_id: child.child_id,
          participation_status: "served",
          attendance_exception_note: null,
          substitution_note: null,
          created_at: new Date().toISOString(),
        });
      });
    const service = state.services.find((row) => row.id === serviceId);
    service.status = "recorded";
    service.service_completed_at = new Date().toISOString();
    writeDemoState(state);
    return demoMealServiceSnapshot(state, serviceId);
  }
  return unwrap(
    await supabase.rpc("rpc_food_record_checked_in_roster", {
      p_meal_service_id: serviceId,
    }),
  );
}

export async function recordMealParticipation({
  serviceId,
  childId,
  participationStatus,
  substitutionNote = "",
  attendanceExceptionNote = "",
}) {
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const snapshot = demoMealServiceSnapshot(state, serviceId);
    const child = snapshot.roster.find((row) => row.child_id === childId);
    if (!child) throw new Error("This child is not on the classroom meal roster.");
    if (snapshot.service.status === "locked") throw new Error("This meal service has been locked after review.");
    if (participationStatus === "substituted" && !cleanText(substitutionNote)) {
      throw new Error("Add a short substitution note before recording this meal.");
    }
    if (["served", "substituted"].includes(participationStatus) && !child.attendance_eligible && !cleanText(attendanceExceptionNote)) {
      throw new Error("Add a short reason when recording a meal for a child who is not marked present or late.");
    }
    const current = state.participations.find((row) => row.meal_service_id === serviceId && row.child_id === childId);
    const next = {
      id: current?.id || demoId("demo-food-participation"),
      meal_service_id: serviceId,
      child_id: childId,
      participation_status: participationStatus,
      substitution_note: participationStatus === "substituted" ? cleanText(substitutionNote) : null,
      attendance_exception_note: ["served", "substituted"].includes(participationStatus) && !child.attendance_eligible
        ? cleanText(attendanceExceptionNote)
        : null,
      created_at: current?.created_at || new Date().toISOString(),
    };
    if (current) Object.assign(current, next);
    else state.participations.push(next);
    const service = state.services.find((row) => row.id === serviceId);
    service.status = "recorded";
    service.service_completed_at = new Date().toISOString();
    writeDemoState(state);
    return demoMealServiceSnapshot(state, serviceId);
  }
  return unwrap(
    await supabase.rpc("rpc_food_record_meal_participation", {
      p_meal_service_id: serviceId,
      p_child_id: childId,
      p_participation_status: participationStatus,
      p_substitution_note: cleanText(substitutionNote) || null,
      p_attendance_exception_note: cleanText(attendanceExceptionNote) || null,
    }),
  );
}

export async function listFoodProgramSetup() {
  if (!isSupabaseConfigured) return normalizeSetup(readDemoState());
  const [programs, categories, templates, eligibilities, accommodations, classrooms, children] = await Promise.all([
    supabase.from("food_programs").select("id, organization_id, name, configuration, is_active, created_at, updated_at").order("is_active", { ascending: false }).order("name"),
    supabase.from("food_eligibility_categories").select("id, organization_id, program_id, code, label, is_active, sort_order, created_at").order("sort_order").order("label"),
    supabase.from("food_menu_templates").select("id, organization_id, program_id, meal_type, name, description, is_active, created_at, items:food_menu_template_items(id, component_label, item_label, portion_note, allergen_note, sort_order)").order("meal_type").order("name"),
    supabase.from("food_child_eligibilities").select("id, organization_id, program_id, child_id, category_id, starts_on, ends_on, note, created_at").order("starts_on", { ascending: false }),
    supabase.from("food_child_meal_accommodations").select("id, organization_id, child_id, label, teacher_instruction, starts_on, ends_on, is_active, created_at").order("created_at", { ascending: false }),
    supabase.from("classrooms").select("id, name, age_group, location_id").order("name"),
    supabase.from("children").select("id, first_name, last_name, classroom_id, allergies, withdrawn_at").is("withdrawn_at", null).order("first_name").order("last_name"),
  ]);
  return {
    programs: unwrap(programs) || [],
    categories: unwrap(categories) || [],
    templates: (unwrap(templates) || []).map((template) => ({
      ...template,
      items: (template.items || []).slice().sort((a, b) => a.sort_order - b.sort_order),
    })),
    eligibilities: unwrap(eligibilities) || [],
    accommodations: unwrap(accommodations) || [],
    classrooms: unwrap(classrooms) || [],
    children: unwrap(children) || [],
  };
}

export async function createFoodProgram({ organizationId, name, actorProfileId = null }) {
  const label = cleanText(name);
  if (!label) throw new Error("Give this food program a name.");
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    state.programs.forEach((program) => { program.is_active = false; });
    const program = {
      id: demoId("demo-food-program"),
      organization_id: organizationId || DEMO_ORGANIZATION_ID,
      name: label,
      configuration: {},
      is_active: true,
      created_by: actorProfileId,
      created_at: new Date().toISOString(),
    };
    state.programs.push(program);
    writeDemoState(state);
    return program;
  }
  ensureLive();
  return unwrap(
    await supabase
      .from("food_programs")
      .insert({
        organization_id: organizationId,
        name: label,
        configuration: {},
        is_active: true,
        created_by: actorProfileId || null,
      })
      .select("id, organization_id, name, configuration, is_active, created_at")
      .single(),
  );
}

export async function createFoodEligibilityCategory({ organizationId, programId, code, label, actorProfileId = null }) {
  const categoryCode = cleanText(code).toLowerCase();
  const categoryLabel = cleanText(label);
  if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(categoryCode)) {
    throw new Error("Use lowercase letters, numbers, hyphens, or underscores for the category code.");
  }
  if (!categoryLabel) throw new Error("Give this category a label.");
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const category = {
      id: demoId("demo-food-category"), organization_id: organizationId || DEMO_ORGANIZATION_ID,
      program_id: programId, code: categoryCode, label: categoryLabel, is_active: true, sort_order: 100,
      created_by: actorProfileId,
    };
    state.categories.push(category);
    writeDemoState(state);
    return category;
  }
  return unwrap(
    await supabase
      .from("food_eligibility_categories")
      .insert({ organization_id: organizationId, program_id: programId, code: categoryCode, label: categoryLabel, created_by: actorProfileId || null })
      .select("id, organization_id, program_id, code, label, is_active, sort_order")
      .single(),
  );
}

export async function createFoodMenuTemplate({ organizationId, programId, mealType, name, description = "", items = [], actorProfileId = null }) {
  const templateName = cleanText(name);
  if (!templateName) throw new Error("Give this menu template a name.");
  const normalizedItems = (items || [])
    .map((item, index) => ({
      component_label: cleanText(item.component_label || item.component || "Menu item") || "Menu item",
      item_label: cleanText(item.item_label || item.label || ""),
      portion_note: cleanText(item.portion_note || "") || null,
      allergen_note: cleanText(item.allergen_note || "") || null,
      sort_order: Number.isFinite(item.sort_order) ? item.sort_order : (index + 1) * 10,
    }))
    .filter((item) => item.item_label);
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const template = {
      id: demoId("demo-food-template"), organization_id: organizationId || DEMO_ORGANIZATION_ID,
      program_id: programId, meal_type: mealType, name: templateName, description: cleanText(description) || null,
      is_active: true, created_by: actorProfileId, items: normalizedItems.map((item) => ({ ...item, id: demoId("demo-food-item") })),
    };
    state.templates.push(template);
    writeDemoState(state);
    return template;
  }
  const template = unwrap(
    await supabase
      .from("food_menu_templates")
      .insert({
        organization_id: organizationId,
        program_id: programId,
        meal_type: mealType,
        name: templateName,
        description: cleanText(description) || null,
        created_by: actorProfileId || null,
      })
      .select("id, organization_id, program_id, meal_type, name, description, is_active")
      .single(),
  );
  if (normalizedItems.length) {
    unwrap(
      await supabase
        .from("food_menu_template_items")
        .insert(normalizedItems.map((item) => ({ ...item, template_id: template.id, created_by: actorProfileId || null }))),
    );
  }
  return { ...template, items: normalizedItems };
}

export async function assignFoodEligibility({ organizationId, programId, childId, categoryId, startsOn, endsOn = null, note = "", actorProfileId = null }) {
  if (!childId || !categoryId || !startsOn) throw new Error("Choose a child, category, and start date.");
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const eligibility = {
      id: demoId("demo-food-eligibility"), organization_id: organizationId || DEMO_ORGANIZATION_ID,
      program_id: programId, child_id: childId, category_id: categoryId, starts_on: startsOn,
      ends_on: endsOn || null, note: cleanText(note) || null, created_by: actorProfileId,
    };
    state.eligibilities.push(eligibility);
    writeDemoState(state);
    return eligibility;
  }
  return unwrap(
    await supabase
      .from("food_child_eligibilities")
      .insert({
        organization_id: organizationId, program_id: programId, child_id: childId, category_id: categoryId,
        starts_on: startsOn, ends_on: endsOn || null, note: cleanText(note) || null, created_by: actorProfileId || null,
      })
      .select("id, organization_id, program_id, child_id, category_id, starts_on, ends_on, note")
      .single(),
  );
}

export async function createFoodMealAccommodation({ organizationId, childId, label, teacherInstruction, startsOn, endsOn = null, actorProfileId = null }) {
  const accommodationLabel = cleanText(label);
  const instruction = cleanText(teacherInstruction);
  if (!childId || !accommodationLabel || !instruction || !startsOn) {
    throw new Error("Choose a child and provide the accommodation, instruction, and start date.");
  }
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const accommodation = {
      id: demoId("demo-food-accommodation"), organization_id: organizationId || DEMO_ORGANIZATION_ID,
      child_id: childId, label: accommodationLabel, teacher_instruction: instruction,
      starts_on: startsOn, ends_on: endsOn || null, is_active: true, created_by: actorProfileId,
    };
    state.accommodations.push(accommodation);
    writeDemoState(state);
    return accommodation;
  }
  return unwrap(
    await supabase
      .from("food_child_meal_accommodations")
      .insert({
        organization_id: organizationId, child_id: childId, label: accommodationLabel,
        teacher_instruction: instruction, starts_on: startsOn, ends_on: endsOn || null, created_by: actorProfileId || null,
      })
      .select("id, organization_id, child_id, label, teacher_instruction, starts_on, ends_on, is_active")
      .single(),
  );
}

export async function planMealService({ programId, classroomId, serviceDate, mealType, menuTemplateId }) {
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const program = state.programs.find((row) => row.id === programId && row.is_active);
    const template = state.templates.find((row) => row.id === menuTemplateId && row.program_id === programId && row.meal_type === mealType && row.is_active);
    if (!program || !template || !classroomId || !serviceDate) throw new Error("Choose the active program, a classroom, date, meal type, and matching menu.");
    let service = state.services.find((row) => row.classroom_id === classroomId && row.service_date === serviceDate && row.meal_type === mealType);
    if (!service) {
      service = { id: demoId("demo-food-service"), organization_id: DEMO_ORGANIZATION_ID, program_id: programId, classroom_id: classroomId, service_date: serviceDate, meal_type: mealType, menu_template_id: menuTemplateId, status: "planned", service_started_at: null, service_completed_at: null, locked_at: null };
      state.services.push(service);
    } else {
      if (service.status === "locked") throw new Error("This meal service has been locked after review.");
      if (state.participations.some((row) => row.meal_service_id === service.id)) throw new Error("A menu cannot be changed after meal participation has been recorded.");
      service.menu_template_id = menuTemplateId;
    }
    writeDemoState(state);
    return service;
  }
  return unwrap(
    await supabase.rpc("rpc_food_plan_meal_service", {
      p_program_id: programId,
      p_classroom_id: classroomId,
      p_service_date: serviceDate,
      p_meal_type: mealType,
      p_menu_template_id: menuTemplateId,
    }),
  );
}

export async function lockFoodMealService({ serviceId, reason }) {
  const reviewReason = cleanText(reason);
  if (!reviewReason) throw new Error("Add a reason before locking this meal service.");
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const service = state.services.find((row) => row.id === serviceId);
    if (!service) throw new Error("Meal service not found.");
    service.status = "locked";
    service.locked_at = new Date().toISOString();
    service.lock_reason = reviewReason;
    writeDemoState(state);
    return { id: service.id, status: service.status, locked_at: service.locked_at };
  }
  return unwrap(
    await supabase.rpc("rpc_food_lock_meal_service", {
      p_meal_service_id: serviceId,
      p_reason: reviewReason,
    }),
  );
}

export async function getFoodProgramReport({ periodStart, periodEnd, programId = null, classroomId = null, limit = 500 }) {
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    const matchingServices = state.services
      .filter((service) => service.service_date >= periodStart && service.service_date <= periodEnd)
      .filter((service) => !programId || service.program_id === programId)
      .filter((service) => !classroomId || service.classroom_id === classroomId)
      .sort((a, b) => `${b.service_date}-${b.meal_type}`.localeCompare(`${a.service_date}-${a.meal_type}`));
    const services = matchingServices.slice(0, limit).map((service) => {
      const snapshot = demoMealServiceSnapshot(state, service.id);
      return {
        service_id: service.id,
        program_id: service.program_id,
        classroom_id: service.classroom_id,
        classroom_name: DEMO_CLASSROOMS.find((row) => row.id === service.classroom_id)?.name || "Classroom",
        service_date: service.service_date,
        meal_type: service.meal_type,
        menu_name: snapshot.service.menu?.name || null,
        status: service.status,
        locked_at: service.locked_at || null,
        ...snapshot.summary,
      };
    });
    const summary = services.reduce(
      (total, service) => ({
        service_count: total.service_count + 1,
        planned_service_count: total.planned_service_count + Number(service.status === "planned"),
        locked_service_count: total.locked_service_count + Number(service.status === "locked"),
        service_without_menu_count: total.service_without_menu_count + Number(!service.menu_name),
        served_count: total.served_count + service.served_count,
        substituted_count: total.substituted_count + service.substituted_count,
        not_served_count: total.not_served_count + service.not_served_count,
        counted_total: total.counted_total + service.counted_total,
        attendance_exception_count: total.attendance_exception_count + service.attendance_exception_count,
      }),
      { service_count: 0, planned_service_count: 0, locked_service_count: 0, service_without_menu_count: 0, served_count: 0, substituted_count: 0, not_served_count: 0, counted_total: 0, attendance_exception_count: 0 },
    );
    return {
      period_start: periodStart, period_end: periodEnd, summary, eligibility_categories: [], services,
      total_service_count: matchingServices.length, truncated: matchingServices.length > limit,
      is_claim_submission: false,
      compliance_notice: "Counts are operational records for review and export. Configure and verify local program requirements before filing a claim.",
    };
  }
  return unwrap(
    await supabase.rpc("rpc_food_program_report", {
      p_period_start: periodStart,
      p_period_end: periodEnd,
      p_program_id: programId || null,
      p_classroom_id: classroomId || null,
      p_limit: limit,
    }),
  );
}

export async function listFoodMealUpdatesForChild(childId, { since = null, until = null, limit = 120 } = {}) {
  if (!childId) return [];
  if (!isSupabaseConfigured) {
    const state = readDemoState();
    return state.participations
      .filter((participation) => participation.child_id === childId && ["served", "substituted"].includes(participation.participation_status))
      .map((participation) => {
        const service = state.services.find((row) => row.id === participation.meal_service_id);
        const template = state.templates.find((row) => row.id === service?.menu_template_id);
        return {
          id: `food-${participation.id}`,
          source_id: participation.id,
          entry_type: "meal",
          occurred_at: service?.service_started_at || participation.created_at,
          service_date: service?.service_date,
          detail: {
            meal: (service?.meal_type || "meal").replace("_", " "),
            amount: participation.participation_status,
            items: template?.name || "Meal service",
            food_program: true,
            substitution_logged: participation.participation_status === "substituted",
          },
          read_only: true,
          source: "food_program",
        };
      })
      .filter((entry) => !since || entry.service_date >= since)
      .filter((entry) => !until || entry.service_date <= until)
      .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at))
      .slice(0, limit);
  }
  try {
    return unwrap(
      await supabase.rpc("rpc_food_list_parent_meal_updates", {
        p_child_id: childId,
        p_since: since || null,
        p_until: until || null,
        p_limit: limit,
      }),
    ) || [];
  } catch (error) {
    // During a rolling deploy, old databases do not yet expose the new RPC.
    // Existing family activity must keep working rather than look empty or
    // fail. Other errors still surface so tenancy/auth problems are visible.
    if (error?.code === "PGRST202" || /could not find the function/i.test(error?.message || "")) return [];
    throw error;
  }
}

// Parent days can be backed by a daily report, food-program participation, or
// both. This merge keeps the route and the family-facing activity timeline
// honest without exposing food-program eligibility or exception information.
export function mergeParentActivityDays(reportRows = [], foodEntries = []) {
  const byDate = new Map();
  for (const report of reportRows || []) {
    if (!report?.report_date) continue;
    byDate.set(report.report_date, { ...report, entries: [...(report.entries || [])] });
  }
  for (const entry of foodEntries || []) {
    const reportDate = entry.service_date || entry.occurred_at?.slice(0, 10);
    if (!reportDate) continue;
    const existing = byDate.get(reportDate) || {
      id: `food-day-${entry.source_id || entry.id}-${reportDate}`,
      child_id: entry.child_id || null,
      classroom_id: null,
      report_date: reportDate,
      overall_mood: null,
      summary_note: null,
      entries: [],
      source: "food_program",
    };
    if (!existing.entries.some((row) => row.id === entry.id)) existing.entries.push(entry);
    byDate.set(reportDate, existing);
  }
  return [...byDate.values()].sort((a, b) => b.report_date.localeCompare(a.report_date));
}

export function mergeFoodEntriesIntoDay(report, foodEntries = [], reportDate) {
  const entries = (foodEntries || []).filter((entry) => (entry.service_date || entry.occurred_at?.slice(0, 10)) === reportDate);
  if (!report && entries.length === 0) return null;
  return {
    ...(report || {
      id: `food-day-${reportDate}`,
      report_date: reportDate,
      overall_mood: null,
      summary_note: null,
      source: "food_program",
    }),
    entries: [...(report?.entries || []), ...entries],
  };
}

export function foodReportRowsToCsv(services = []) {
  const header = [
    "Service date", "Classroom", "Meal", "Menu", "Status", "Served", "Substituted", "Not served", "Counted total", "Attendance exceptions",
  ];
  const escapeCell = (value) => {
    const raw = String(value ?? "");
    const safe = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const rows = (services || []).map((service) => [
    service.service_date,
    service.classroom_name,
    foodMealLabel(service.meal_type),
    service.menu_name || "Not selected",
    service.status,
    service.served_count,
    service.substituted_count,
    service.not_served_count,
    service.counted_total,
    service.attendance_exception_count,
  ]);
  return [header, ...rows].map((row) => row.map(escapeCell).join(",")).join("\n");
}
